// Videodauer für Export/Upload sicherstellen (Audit H6).
//
// `useVideoStore.durations[idx]` füllt nur der VideoPlayer des gerade
// geöffneten Assets (loadedmetadata). Nach Reload → „Continue" → Summary →
// „Upload all" fehlte die Dauer für jedes seitdem nicht geöffnete Asset mit
// Cloud-Video; die Länge fiel still auf targetDuration bzw. 30 s zurück —
// Voice-WAV, Premix und MP4-Tonspur zu kurz, Video-Ton abgeschnitten, in
// TIMECODES.csv fehlte die Videozeile.
//
// Zwei Ebenen: die Session-Persistenz speichert bekannte Dauern mit
// (useSessionPersistence), und vor jedem Export/Upload wird eine fehlende
// Dauer hier per `<video preload="metadata">` nachgeladen. Die Entscheidung
// „welches Asset braucht eine Dauer" ist pur und getestet.

import type { Asset } from '@shared/types';
import { streamVideoUrl } from '@/lib/api';
import { useVideoStore } from '@/stores/video';

/** Indizes der Assets, deren Timeline am Cloud-Video hängt, für die aber
 *  noch keine Dauer bekannt ist. Ein lokal geladenes Video (Blob) hat seine
 *  Dauer immer schon über den Player; Assets ohne `videoPath` brauchen keine. */
export function assetsNeedingVideoDuration(
  assets: ReadonlyArray<Pick<Asset, 'videoPath'> | undefined>,
  durations: Record<number, number>,
  blobs: Record<number, Blob | null>,
  only?: ReadonlyArray<number>,
): number[] {
  const candidates = only ?? assets.map((_, i) => i);
  const out: number[] = [];
  for (const idx of candidates) {
    const asset = assets[idx];
    if (!asset?.videoPath) continue;
    if (blobs[idx]) continue;
    const d = durations[idx];
    if (typeof d === 'number' && Number.isFinite(d) && d > 0) continue;
    out.push(idx);
  }
  return out;
}

/** Nur die Dauern behalten, die einen Reload überleben: Cloud-Videos
 *  (`videoPath`) streamen beim nächsten Besuch wieder, eigene Blob-Videos
 *  sind dann weg — ihre Dauer würde eine Timeline-Grenze ohne Video
 *  vortäuschen (siehe cleanupOrphanedCustomDurations). */
export function persistableVideoDurations(
  durations: Record<number, number>,
  assets: ReadonlyArray<Pick<Asset, 'videoPath'> | undefined>,
): Record<number, number> {
  const out: Record<number, number> = {};
  for (const [k, d] of Object.entries(durations)) {
    const idx = Number(k);
    if (!assets[idx]?.videoPath) continue;
    if (typeof d !== 'number' || !Number.isFinite(d) || d <= 0) continue;
    out[idx] = d;
  }
  return out;
}

/** Dauer eines Videos über ein unsichtbares `<video preload="metadata">`.
 *  Cookies (Media-Session) gehen same-origin automatisch mit. Löst mit 0
 *  auf, wenn Metadaten nicht kommen (Fehler/Timeout) — der Aufrufer
 *  behandelt 0 wie „unbekannt". */
export function probeVideoDuration(url: string, timeoutMs = 20000): Promise<number> {
  return new Promise<number>((resolve) => {
    const v = document.createElement('video');
    let done = false;
    const finish = (d: number) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      v.removeAttribute('src');
      try { v.load(); } catch { /* ignore */ }
      resolve(Number.isFinite(d) && d > 0 ? d : 0);
    };
    const timer = window.setTimeout(() => finish(0), timeoutMs);
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => finish(v.duration);
    v.onerror = () => finish(0);
    v.src = url;
  });
}

/** Einzel-Variante für die Views, die nur ihr aktives Asset kennen: schreibt
 *  die Dauer unter dem echten kombinierten Index `assetIdx`. */
export async function ensureVideoDurationFor(
  toolId: string | null,
  assetIdx: number,
  asset: Pick<Asset, 'videoPath'> | null | undefined,
): Promise<void> {
  if (!asset) return;
  const sparse: Array<Pick<Asset, 'videoPath'> | undefined> = [];
  sparse[assetIdx] = asset;
  await ensureVideoDurations(toolId, sparse, [assetIdx]);
}

/** Fehlende Dauern für `indices` nachladen und in den Video-Store schreiben.
 *  Läuft die Assets sequenziell durch (ein Stream nach dem anderen). */
export async function ensureVideoDurations(
  toolId: string | null,
  assets: ReadonlyArray<Pick<Asset, 'videoPath'> | undefined>,
  indices: ReadonlyArray<number>,
): Promise<void> {
  if (!toolId) return;
  const video = useVideoStore.getState();
  const missing = assetsNeedingVideoDuration(assets, video.durations, video.blobs, indices);
  for (const idx of missing) {
    const path = assets[idx]?.videoPath;
    if (!path) continue;
    const d = await probeVideoDuration(streamVideoUrl(toolId, path));
    if (d > 0) useVideoStore.getState().setDuration(idx, d);
  }
}
